import { z } from 'zod';
import { AssetMergeAssetTypeSchema } from './asset-merge.schema';

export const CopilotMergeAssetByIdSchema = z.object({
  assetId: z.string().min(1),
  type: AssetMergeAssetTypeSchema.optional(),
});

export const CopilotMergeAssetByPathSchema = z.object({
  path: z.string().min(1),
  domain: z.string().min(1),
  type: AssetMergeAssetTypeSchema,
  method: z.string().min(1).optional(),
});

export const CopilotMergeAssetResolvedSchema = z.object({
  assetId: z.string().min(1),
  path: z.string().min(1),
  domain: z.string().min(1),
  type: AssetMergeAssetTypeSchema,
  method: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
});

export const CopilotMergeNewSurvivorSchema = z.object({
  path: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
  hash: z.string().min(1).optional(),
});

export const CopilotMergeAssetInputSchema = z.union([
  CopilotMergeAssetResolvedSchema,
  CopilotMergeAssetByPathSchema,
  CopilotMergeAssetByIdSchema,
]);

export const COPILOT_ASSET_MERGE_MAX_LOSERS = 500;

export const CopilotMergeProposalResolvedSchema = z.object({
  type: AssetMergeAssetTypeSchema,
  survivor: CopilotMergeAssetResolvedSchema,
  losers: z
    .array(CopilotMergeAssetResolvedSchema)
    .min(1)
    .max(COPILOT_ASSET_MERGE_MAX_LOSERS),
});

const hasResolvedMergeAsset = (
  value: z.infer<typeof CopilotMergeAssetInputSchema> | undefined,
): value is z.infer<typeof CopilotMergeAssetResolvedSchema> =>
  CopilotMergeAssetResolvedSchema.safeParse(value).success;

export const CopilotMergeProposalInputSchema = z
  .object({
    type: AssetMergeAssetTypeSchema.optional().describe(
      'ENDPOINT or WEBPAGE. Required when identifying assets by path + domain.',
    ),
    survivorId: z
      .string()
      .min(1)
      .optional()
      .describe(
        'Existing asset id that remains. Infer from destination or keep language.',
      ),
    loserIds: z
      .array(z.string().min(1))
      .min(1)
      .max(COPILOT_ASSET_MERGE_MAX_LOSERS)
      .optional()
      .describe(
        `Absorbed asset ids, at most ${COPILOT_ASSET_MERGE_MAX_LOSERS}. For a merge, pass every loser in one call. For a revise, pass the one asset being templated.`,
      ),
    survivor: CopilotMergeAssetInputSchema.optional().describe(
      'Remaining asset as assetId, or path + domain + type.',
    ),
    losers: z
      .array(CopilotMergeAssetInputSchema)
      .min(1)
      .max(COPILOT_ASSET_MERGE_MAX_LOSERS)
      .optional()
      .describe(
        `Absorbed assets as assetId, or path + domain + type. At most ${COPILOT_ASSET_MERGE_MAX_LOSERS}.`,
      ),
    newSurvivor: CopilotMergeNewSurvivorSchema.optional().describe(
      'New remaining path or url. Required when revising one asset into a covering template, or when merging into a new path.',
    ),
  })
  .refine(
    (value) => {
      if (
        hasResolvedMergeAsset(value.survivor) &&
        (value.losers?.length ?? 0) > 0
      ) {
        return (
          value.losers?.every((loser) => hasResolvedMergeAsset(loser)) ?? false
        );
      }

      const hasIdLosers = (value.loserIds?.length ?? 0) > 0;
      const hasRefLosers = (value.losers?.length ?? 0) > 0;
      const hasExistingSurvivor = Boolean(value.survivorId || value.survivor);
      const hasNewSurvivor = Boolean(value.newSurvivor);

      return (
        (hasIdLosers || hasRefLosers) && hasExistingSurvivor !== hasNewSurvivor
      );
    },
    {
      message:
        'Provide survivorId or survivor plus losers, or newSurvivor plus losers',
    },
  );

export type CopilotMergeAssetById = z.infer<typeof CopilotMergeAssetByIdSchema>;
export type CopilotMergeAssetByPath = z.infer<
  typeof CopilotMergeAssetByPathSchema
>;
export type CopilotMergeAssetResolved = z.infer<
  typeof CopilotMergeAssetResolvedSchema
>;
export type CopilotMergeProposalResolved = z.infer<
  typeof CopilotMergeProposalResolvedSchema
>;
export type CopilotMergeProposalInput = z.infer<
  typeof CopilotMergeProposalInputSchema
>;

export const formatResolvedMergeAsset = (
  asset: CopilotMergeAssetResolved,
): string => {
  const route = [asset.method, asset.url ?? asset.path]
    .filter(Boolean)
    .join(' ');

  return `${route} (\`${asset.assetId}\` · ${asset.domain})`;
};

export const COPILOT_NEW_SURVIVOR_ASSET_ID = 'new';

export const isCopilotMergeReviseProposal = (
  proposal: CopilotMergeProposalResolved,
): boolean =>
  proposal.losers.length === 1 &&
  proposal.survivor.assetId === COPILOT_NEW_SURVIVOR_ASSET_ID;

export const formatResolvedMergeProposalMarkdown = (
  proposal: CopilotMergeProposalResolved,
): string => {
  const absorbed = proposal.losers
    .map((loser) => `- ${formatResolvedMergeAsset(loser)}`)
    .join('\n');

  if (isCopilotMergeReviseProposal(proposal)) {
    return `**Revised path**\n${formatResolvedMergeAsset(proposal.survivor)}\n\n**Original**\n${absorbed}`;
  }

  return `**Keep**\n${formatResolvedMergeAsset(proposal.survivor)}\n\n**Absorb**\n${absorbed}`;
};

export const CopilotRevertPreviewAssetSchema = z.object({
  assetId: z.string().min(1),
  type: AssetMergeAssetTypeSchema,
  path: z.string().min(1).optional(),
  domain: z.string().min(1).optional(),
  method: z.string().min(1).optional(),
  url: z.string().min(1).optional(),
});

export const CopilotRevertPreviewSchema = z.object({
  historyIds: z.array(z.string().min(1)).optional(),
  assetIds: z.array(z.string().min(1)).optional(),
  restored: z.array(CopilotRevertPreviewAssetSchema),
  remaining: z.array(CopilotRevertPreviewAssetSchema),
});

export type CopilotRevertPreviewAsset = z.infer<
  typeof CopilotRevertPreviewAssetSchema
>;
export type CopilotRevertPreview = z.infer<typeof CopilotRevertPreviewSchema>;

export const formatResolvedRevertAsset = (
  asset: CopilotRevertPreviewAsset,
): string => {
  const route = [asset.method, asset.url ?? asset.path]
    .filter(Boolean)
    .join(' ');
  const suffix = [asset.assetId, asset.domain].filter(Boolean).join(' · ');

  if (route && suffix) {
    return `${route} (\`${suffix}\`)`;
  }

  if (route) {
    return `${route} (\`${asset.assetId}\`)`;
  }

  return `\`${asset.assetId}\``;
};

export const formatResolvedRevertPreviewMarkdown = (
  preview: CopilotRevertPreview,
): string => {
  const restored = preview.restored
    .map((asset) => `- ${formatResolvedRevertAsset(asset)}`)
    .join('\n');
  const remaining = preview.remaining
    .map((asset) => `- ${formatResolvedRevertAsset(asset)}`)
    .join('\n');

  return `**Restore**\n${restored || '- None'}\n\n**Un-merge from**\n${remaining || '- None'}`;
};
